import { LABELS, type LabelKey } from '../../config/labels';
import { useBilingualEnabled } from '../../config/useBilingualEnabled';

interface StarterChipsProps {
  keys: readonly LabelKey[];
  onPick: (question: string) => void;
  disabled?: boolean;
  /** One scrolling row of small chips, for under a conversation already under way. */
  compact?: boolean;
}

/**
 * The Assistant's starter questions. Each chip is ONE language and sends only that text:
 * the assistant replies in Chinese whenever a question contains any Chinese, so the old
 * "中文 English" chip answered English speakers in Chinese. On a bilingual brand the
 * Chinese questions and the English ones are separate rows and the parent taps either;
 * an English-only brand gets the English row alone.
 */
export function StarterChips({ keys, onPick, disabled, compact }: StarterChipsProps) {
  const bilingual = useBilingualEnabled();
  const rows: { lang: 'zh' | 'en'; caption: string; questions: string[] }[] = [
    ...(bilingual ? [{ lang: 'zh' as const, caption: '中文', questions: keys.map((k) => LABELS[k].zh) }] : []),
    { lang: 'en', caption: 'English', questions: keys.map((k) => LABELS[k].en) },
  ];

  if (compact) {
    return (
      <div className="flex gap-2 overflow-x-auto pb-1.5 -mx-1 px-1" data-testid="genie-chips-compact">
        {rows.flatMap((row) => row.questions.map((q) => (
          <button
            key={`${row.lang}:${q}`}
            type="button"
            lang={row.lang === 'zh' ? 'zh-CN' : 'en'}
            onClick={() => onPick(q)}
            disabled={disabled}
            className="flex-shrink-0 px-2.5 py-1 text-xs rounded-full border border-gray-200 text-gray-500 hover:text-gray-800 hover:bg-gray-50 whitespace-nowrap disabled:opacity-50"
          >
            {q}
          </button>
        )))}
      </div>
    );
  }

  return (
    <div className="space-y-3 mb-4" data-testid="genie-chips">
      {rows.map((row) => (
        <div key={row.lang}>
          {bilingual && <p className="text-[11px] font-medium text-gray-400 mb-1.5">{row.caption}</p>}
          <div className="flex flex-wrap gap-2">
            {row.questions.map((q) => (
              <button
                key={q}
                type="button"
                lang={row.lang === 'zh' ? 'zh-CN' : 'en'}
                onClick={() => onPick(q)}
                disabled={disabled}
                className="px-3 py-1.5 text-sm rounded-full border border-gray-300 text-gray-700 hover:bg-gray-50 text-left disabled:opacity-50"
              >
                {q}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
